import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Alert, Button} from "react-bootstrap";
import axios from "axios";
import {API_URL} from "../../../../config";
import {createIdempotencyKey} from "./rsoSalesForecastHelpers";
import {monthDate, monthKey, monthYear, unsendableChangeNote} from "./rsoForecastWindowHelpers";
import RsoWithdrawDialog, {withdrawLinkLabel, withdrawnNotice, withdrawOutcomeUnknown, withdrawUnconfirmedNotice} from "./RsoWithdrawDialog";
import {isSignInExpired} from "./RsoSignInExpired";

const endpoint = `${API_URL}/Dashboard/api/RsoPropertyForecast`;
const standardsEndpoint = `${API_URL}/Dashboard/api/RsoMaterialStandards`;
const kinds = ["unsent_change", "sent_change", "draft"];
const validId = (value) => Number.isSafeInteger(Number(value)) && Number(value) > 0;
const named = (value, fallback) => typeof value === "string" && value.trim() ? value.trim() : fallback;
// Each item carries only its own kind's fields. A draft always has a month; an older saved change may not.
const validItem = (item) => item && kinds.includes(item.kind) && validId(item.contactInfoId)
  && (item.kind === "draft" ? !!monthKey(item.forecastMonth) && Number.isSafeInteger(item.draftRevision) && item.draftRevision >= 0
    : validId(item.changeId) && (item.forecastMonth == null || !!monthKey(item.forecastMonth)));
const monthPart = (item, separator) => (monthKey(item.forecastMonth) ? `${separator}${monthYear(item.forecastMonth)}` : "");
const itemKey = (item) => `${item.kind}:${item.contactInfoId}:${item.changeId ?? item.forecastMonth}`;
const turns = (value) => Number.isSafeInteger(Number(value)) && value !== null
  ? `${Number(value).toLocaleString()} ${Number(value) === 1 ? "unit turn" : "unit turns"}` : "";
// Sent changes are done; they are listed only so Sales can still withdraw them.
const sentTitle = "Sent forecasts to Purchasing";

// Unsent and sent material changes, and drafts for months that can't be changed now.
// None of these depend on the month being viewed, so the list is always reachable from the checklist.
// hideLoadError: the page is already showing one message because the whole checklist could not load.
// onSignInExpired: called when the list read was refused because the sign-in expired; the page then shows its one
// sign-in message and hides this panel's load error.
export default function RsoNeedsActionPanel({token, entryWindow, reloadKey, onChanged, onSignInExpired, hideLoadError = false}) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("loading");
  const [confirming, setConfirming] = useState(null);
  // The sent change whose Withdraw dialog is open.
  const [withdrawing, setWithdrawing] = useState(null);
  const [working, setWorking] = useState(null);
  const [notice, setNotice] = useState({variant: "", text: ""});
  const busy = useRef(false);
  const generation = useRef(0);
  // A retried draft discard reuses its request ID, so a lost response can't turn into a conflict.
  const discardIds = useRef({});
  // The sent change whose withdrawal got no confirmed answer (no reply, or a 5xx).
  const unconfirmedWithdrawal = useRef(null);
  const config = useMemo(() => ({headers: {Authorization: `Bearer ${token}`}, timeout: 30000}), [token]);
  // Read through a ref, so a new callback on each render never reloads the list.
  const signInExpiredCallback = useRef(onSignInExpired);
  signInExpiredCallback.current = onSignInExpired;
  const load = useCallback(async () => {
    const current = ++generation.current;
    try {
      const response = await axios.get(`${endpoint}/pending-actions`, config);
      if (current !== generation.current) return;
      if (!Array.isArray(response.data?.items)) throw new Error("Invalid pending actions");
      setItems(response.data.items.filter(validItem));setStatus("ready");
    } catch (failure) {
      if (current !== generation.current) return;
      if (isSignInExpired(failure)) signInExpiredCallback.current?.();
      setStatus("error");
    }
  }, [config]);
  useEffect(() => {load(); return () => {generation.current += 1;};}, [load, reloadKey]);
  // A refreshed list that no longer has the sent change (or can't be read) closes its Withdraw dialog, but never mid-request.
  useEffect(() => {
    if (withdrawing && !working && (status === "error" || (status === "ready" && !items.some((item) => itemKey(item) === itemKey(withdrawing))))) setWithdrawing(null);
  }, [items, status, withdrawing, working]);
  // Once a fresh list no longer has that sent change, the withdrawal most likely went through, so Sales must call Purchasing.
  useEffect(() => {
    const pending = unconfirmedWithdrawal.current;
    if (!pending || status !== "ready" || items.some((item) => itemKey(item) === itemKey(pending))) return;
    unconfirmedWithdrawal.current = null;
    setNotice({variant: "warning", text: withdrawUnconfirmedNotice({propertyName: pending.propertyName, toName: pending.toName})});
  }, [items, status]);
  const run = async (item, action) => {
    if (busy.current) return;
    const key = itemKey(item);
    busy.current = true;setWorking(key);setNotice({variant: "", text: ""});
    if (action === "withdraw") unconfirmedWithdrawal.current = null;
    const property = named(item.propertyName, "this property");
    try {
      if (action === "send" || action === "discard" || action === "withdraw") {
        const response = await axios.post(`${standardsEndpoint}/${action}`, {contactInfoId: Number(item.contactInfoId), changeId: Number(item.changeId)}, config);
        const expected = {send: ["sent", "publishedAtUtc"], discard: ["discarded", "discardedAtUtc"], withdraw: ["withdrawn", "withdrawnAtUtc"]}[action];
        if (Number(response.data?.contactInfoId) !== Number(item.contactInfoId) || Number(response.data?.changeId) !== Number(item.changeId)
          || response.data?.requestStatus !== expected[0] || !response.data?.[expected[1]]) throw new Error("Not confirmed");
        setNotice({variant: "success", text: action === "send" ? `Material change for ${property} sent to Purchasing.`
          : action === "discard" ? `Material change for ${property} discarded. The previous material has been restored.`
            : withdrawnNotice({propertyName: property, fromName: item.fromName, toName: item.toName})});
      } else {
        const params = {contactInfoId: Number(item.contactInfoId), forecastMonth: monthDate(item.forecastMonth),
          expectedDraftRevision: item.draftRevision, requestId: discardIds.current[key] || createIdempotencyKey()};
        discardIds.current[key] = params.requestId;
        const response = await axios.delete(`${endpoint}/draft`, {...config, params});
        if (Number(response.data?.contactInfoId) !== params.contactInfoId || monthKey(response.data?.forecastMonth) !== monthKey(params.forecastMonth)
          || !Array.isArray(response.data?.materials) || response.data.materials.length) throw new Error("Not confirmed");
        delete discardIds.current[key];
        setNotice({variant: "success", text: `Draft for ${property} · ${monthYear(item.forecastMonth)} discarded.`});
      }
      setConfirming(null);setWithdrawing(null);
      onChanged?.();
      await load();
    } catch (failure) {
      if (action === "withdraw" && withdrawOutcomeUnknown(failure)) unconfirmedWithdrawal.current = item;
      const message = failure?.response?.data?.message;
      setNotice({variant: "danger", text: typeof message === "string" && message ? message
        : failure?.response ? "Could not complete this action. Try again."
          : "Could not confirm whether this worked. Refresh the list before trying again."});
    } finally {busy.current = false;setWorking(null);}
  };
  if (status === "error" && hideLoadError) return null;
  if (status === "error") return <Alert variant="warning" className="rpf-needs-action">Could not check for material changes and drafts that need your action.{" "}
    <Button variant="link" className="p-0 align-baseline" onClick={load}>Try again</Button></Alert>;
  if (!items.length) return notice.text ? <Alert variant={notice.variant} role={notice.variant === "danger" ? "alert" : "status"}>{notice.text}</Alert> : null;
  const todo = items.filter((item) => item.kind !== "sent_change");
  const sent = items.filter((item) => item.kind === "sent_change");
  const renderItem = (item) => {
    const key = itemKey(item);
    const property = named(item.propertyName, `Property #${item.contactInfoId}`);
    const disabled = !!working;
    const note = item.kind === "unsent_change" && item.canSend !== true ? unsendableChangeNote(entryWindow, item.forecastMonth, item.fromName) : "";
    const change = item.kind !== "draft" && (named(item.fromName, "") || named(item.toName, ""))
      ? [named(item.fromName, "Previous material"), named(item.toName, "new material")].join(" → ") : "";
    return <li key={key} className="rpf-needs-action-item">
      <p className="mb-1"><strong>{property}</strong>{monthPart(item, " · ")}
        {item.kind === "draft" ? " · Draft saved, can't be sent now"
          : <>{change && ` · ${change}`}{turns(item.unitsTurning) && ` · ${turns(item.unitsTurning)}`}
            {item.kind !== "sent_change" && " · Saved, not sent to Purchasing"}</>}
        {/* Withdrawing a sent change should be very rare, so it is a faint link at the end of the line that opens a dialog telling Sales to call Purchasing. */}
        {item.kind === "sent_change" && <Button variant="link" size="sm" className="rpf-withdraw-link" disabled={disabled}
          aria-label={withdrawLinkLabel({propertyName: property, forecastMonth: item.forecastMonth})}
          onClick={() => {setWithdrawing(item);setConfirming(null);setNotice({variant: "", text: ""});}}>Withdraw…</Button>}</p>
      {note && <p className="small mb-1">{note}</p>}
      {item.kind === "sent_change" ? null : confirming === key ? <div className="rpf-needs-action-confirm">
        <p className="mb-1">{item.kind === "draft" ? "Discard this draft? It was never sent to Purchasing."
          : "Discard this saved change and restore the previous material?"}</p>
        <Button variant="danger" size="sm" disabled={disabled}
          onClick={() => run(item, item.kind === "draft" ? "draft" : "discard")}>
          {working === key ? "Please wait…" : item.kind === "draft" ? "Discard draft" : "Discard change"}</Button>{" "}
        <Button variant="outline-secondary" size="sm" disabled={disabled} onClick={() => setConfirming(null)}>{item.kind === "draft" ? "Keep draft" : "Keep change"}</Button>
      </div> : <div>
        {/* A Send can fail (for example, while the Purchasing feed is off), so Discard is always offered too. */}
        {item.kind === "unsent_change" && item.canSend === true && <><Button variant="success" size="sm" className="rpf-send-purchasing" disabled={disabled}
          aria-label={`Send the ${property} material change to Purchasing`} onClick={() => run(item, "send")}>{working === key ? "Sending…" : "Send to Purchasing"}</Button>{" "}</>}
        <Button variant="outline-danger" size="sm" disabled={disabled}
            aria-label={`${item.kind === "draft" ? "Discard the draft" : "Discard the material change"} for ${property}${monthPart(item, ", ")}`}
            onClick={() => {setConfirming(key);setNotice({variant: "", text: ""});}}>
            {item.kind === "draft" ? "Discard draft" : "Discard change"}</Button>
      </div>}
    </li>;
  };
  const title = todo.length ? "Needs your action" : sentTitle;
  // Sent forecasts need nothing from Sales, so on their own they get a calm box, not the yellow action one.
  return <section className={`rpf-needs-action${todo.length ? "" : " rpf-needs-action--sent"}`} aria-label={title}>
    <h5>{title}</h5>
    {/* While the Withdraw dialog is open, its error is shown inside the dialog instead. */}
    {notice.text && !withdrawing && <Alert variant={notice.variant} role={notice.variant === "danger" ? "alert" : "status"}>{notice.text}</Alert>}
    <ul className="rpf-needs-action-list">{(todo.length ? todo : sent).map(renderItem)}</ul>
    {!!todo.length && !!sent.length && <>
      <h6 className="rpf-needs-action-subheading">{sentTitle}</h6>
      <ul className="rpf-needs-action-list">{sent.map(renderItem)}</ul>
    </>}
    {withdrawing && <RsoWithdrawDialog key={itemKey(withdrawing)} change={withdrawing} working={working === itemKey(withdrawing)} disabled={!!working}
      error={notice.variant === "danger" ? notice.text : ""} onCancel={() => setWithdrawing(null)} onConfirm={() => run(withdrawing, "withdraw")}
      onRefresh={() => {onChanged?.();load();}} refreshLabel="Refresh the list" />}
  </section>;
}
